import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CircularProgress, Box, Typography } from '@mui/material';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';

const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [redirecting, setRedirecting] = useState(false);
  const location = useLocation();

  // Extended loading state - give the auth context more time to initialize
  // This prevents flickering between routes during authentication checks
  const [extendedLoading, setExtendedLoading] = useState(true);
  
  // Check for valid token in localStorage to avoid unnecessary redirects during auth state changes
  // Import apiService at the top of the file if not already there
  const hasValidToken = authService.hasValidAuthToken();
  
  useEffect(() => {
    console.log(`ProtectedRoute: Auth state changed - isLoading: ${isLoading}, isAuthenticated: ${isAuthenticated}, hasValidToken: ${hasValidToken}`);
    
    // Extended loading logic - if we have a valid token but auth is still loading,
    // give it more time to complete initialization
    if (hasValidToken && isLoading) {
      console.log('ProtectedRoute: Valid token found but auth still loading, extending wait time');
      setExtendedLoading(true);
      
      // Start a longer timer to handle extended loading for token-present cases
      const timer = setTimeout(() => {
        setExtendedLoading(false);
        console.log('ProtectedRoute: Extended loading period ended');
      }, 1500); // Longer time for token-present cases
      
      return () => clearTimeout(timer);
    } else {
      // Standard loading for non-token cases or when auth is complete
      const timer = setTimeout(() => {
        setExtendedLoading(false);
      }, 800); // Standard time for other cases
      
      // If authentication is determined and the user is not authenticated, 
      // prepare to redirect
      if (!isLoading && !isAuthenticated && !hasValidToken) {
        console.log('ProtectedRoute: Not authenticated and no valid token, preparing to redirect to login');
        setRedirecting(true);
      }
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, isAuthenticated, hasValidToken]);

  // If we're still in the loading state, show a loading indicator
  if (isLoading || extendedLoading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        flexDirection="column"
        minHeight="100vh"
      >
        <CircularProgress size={50} thickness={4} />
        <Typography variant="h6" mt={2}>
          Loading your data...
        </Typography>
      </Box>
    );
  }

  // If we're not authenticated and done loading, redirect to login
  // But if we have a valid token, give it a bit more time in case auth state is still initializing
  if ((!isAuthenticated || !user) && !isLoading && (!hasValidToken || !extendedLoading)) {
    console.log('ProtectedRoute: Redirecting to login', {
      isAuthenticated,
      hasUser: !!user,
      hasValidToken,
      isLoading,
      extendedLoading
    });
    
    // Store the attempted location so we can redirect back after login
    const from = encodeURIComponent(location.pathname);
    return <Navigate to={`/login?redirect=${from}`} />;
  }

  console.log('ProtectedRoute: Authenticated, rendering protected content');
  return <Outlet />;
};

export default ProtectedRoute;